import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-39d02746"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  id: "staff-list-page",
  class: "main-content-wrapper"
}
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", { class: "main-content-page-title" }, "予約通知", -1))
const _hoisted_3 = { class: "list-staff-wrapper pr-6" }
const _hoisted_4 = { class: "list-staff-body" }
const _hoisted_5 = { class: "flex justify-content-between items-center mt-3 mb-3" }
const _hoisted_6 = { class: "grid grid-nogutter grid-header" }
const _hoisted_7 = { class: "grid-wrapper cursor-pointer" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "customer-title" }
const _hoisted_10 = { key: 3 }
const _hoisted_11 = {
  key: 1,
  class: "col text-center"
}
const _hoisted_12 = { class: "loading" }
const _hoisted_13 = {
  key: 2,
  class: "col text-center empty-result"
}

import { get, set } from "@vueuse/core";
import { computed, inject, onBeforeMount, ref } from "vue";
import type { ApiBasePaginatorReq, ApiBaseReq } from "@/clinic/typings";
import { PageState } from "primevue/paginator";
import Paginator from "@/clinic/components/atoms/Paginator/index.vue";
import { DEFAULT_PAGINATOR_ROWS } from "@/clinic/typings/constants/paginator";
import BookingNotificationsService from "@/clinic/services/booking.notifications.service";
import Tag from "primevue/tag";
import { useStore } from "vuex";

type IColumn = {
  field: string;
  header: string;
  class?: string;
  style?: string;
  icon?: unknown;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

/**
 * 予約通知一覧画面コンポーネント
 * 予約通知の表示、ページネーション、全て既読処理などを行う画面
 *
 * @component
 */
const store = useStore();

/** ローディング中かどうかを示すフラグ */
const loading = ref(false);

/** Paginatorコンポーネントで使用される現在の先頭インデックス */
const first = ref(0);
/** 1ページあたりの表示件数 */
const rows = ref(DEFAULT_PAGINATOR_ROWS);
/** 通知の総件数 */
const totalCount = ref(0);
/** 現在のページ番号 */
const page = ref(0);
/** ページングを制御する上部のPaginatorコンポーネント参照 */
const pgTop = ref<InstanceType<typeof Paginator> | null>(null);
/** ページングを制御する下部のPaginatorコンポーネント参照 */
const pgBot = ref<InstanceType<typeof Paginator> | null>(null);

/** クリニックID */
const clinicId = ref(0);

/** 取得した予約通知リスト */
const bookingNotifications = ref([]);

/**
 * 通知を表示する列定義
 *
 * @type {import('vue').Ref<IColumn[]>}
 */
const columns = ref<IColumn[]>([
  { field: "id", header: "通知ID", class: "col-1" },
  { field: "title", header: "タイトル", class: "col-3" },
  {
    field: "booking_info",
    header: "詳細",
    class: "col-3 pr-3",
  },
  { field: "action_type_name", header: "予約アクション", class: "col-3 px-5" },
  { field: "created_at", header: "日時", class: "col-2 px-5" },
]);

/**
 * 通常の予約通知に対して、アクション種別に応じた背景クラスを返却する
 *
 * @function
 * @returns {Object} キー: アクション種別, 値: CSSクラス名 のオブジェクト
 */
const bookingNormalBackgroundColor = () => {
  return {
    create_booking: "bg-will-come",
    update_booking: "bg-booked",
    update_booking_status: "bg-booked",
    customer_cancel_booking: "bg-cancel",
    admin_cancel_booking: "bg-cancel",
    admin_destroy_booking: "bg-visited",
    admin_confirm_request: "bg-booking-request",
    create_routine_booking: "bg-cancel-not-notice",
    update_routine_booking: "bg-booking-instagram",
  };
};

/**
 * クリニックIDをパラメータ形式で返す非同期関数
 *
 * @async
 * @function
 * @returns {Promise<ApiBaseReq>} clinic_id を含むパラメータオブジェクト
 */
const clinicIdParams = async (): Promise<ApiBaseReq> => ({ clinic_id: get(await get(clinicId)) });

/**
 * ページング関連のパラメータを計算して返すcomputedプロパティ
 *
 * @constant
 * @type {import('vue').ComputedRef<ApiBasePaginatorReq>}
 */
const paginatorParams = computed(
  (): ApiBasePaginatorReq =>
    ({
      page: get(page) + 1, // バックエンドは1ベースのページ番号を期待
      rows: get(rows),
      sort_column: "created_at",
      sort_order: "desc",
    } as ApiBasePaginatorReq)
);

/**
 * Paginatorコンポーネントからのイベントでページングを行い、一覧を再取得する
 *
 * @async
 * @function
 * @param {string} fromId - イベントを発火したPaginatorのID
 * @param {PageState} payload - ページング情報 (newPageなど)
 * @returns {Promise<void>}
 */
const paging = async (fromId: string, { page: newPage, first: newFirst }: PageState) => {
  // 現在のページと同じ場合は何もしない
  if (newPage === get(page)) return;

  // ページ番号を更新
  set(page, newPage);

  // firstの値を更新（PrimeVueから渡された値を使用）
  set(first, newFirst);

  await loadBookingNotifications();
};

/**
 * 予約通知リストをAPIから取得し、画面に表示する
 *
 * @async
 * @function
 * @returns {Promise<void>}
 */
const loadBookingNotifications = async () => {
  set(loading, true);
  const responses = await BookingNotificationsService.fetch({ ...(await clinicIdParams()), ...get(paginatorParams) });
  set(bookingNotifications, responses.data.value.booking_notifications);
  set(totalCount, responses.data.value.meta.total_count);
  set(loading, false);
};

/**
 * 初期表示時にクリニックIDを取得し、予約通知をロードする
 *
 * @async
 * @function
 * @returns {Promise<void>}
 */
const loadList = async () => {
  set(clinicId, await inject("clinicId"));
  await loadBookingNotifications();
};

/**
 * 通知をクリックした際に、通知詳細をVuexストアに反映し、既読フラグを更新する
 *
 * @async
 * @function
 * @param {Object} notification - 通知オブジェクト
 * @returns {Promise<void>}
 */
const bookingDetail = async (notification: Record<string, unknown>) => {
  notification.viewed = true;
  const storeValues = {
    booking_id: notification["booking_id"],
    notification_id: notification["id"],
  };
  store.commit("booking/setNotificationsBookingDetails", storeValues);
};

/**
 * 通知一覧のうち、予約アラート以外の通知を全て既読にし、再度一覧を読み込む
 * また、ヘッダーバッジ（未読数表示）の再描画イベントも発行する
 *
 * @async
 * @function
 * @returns {Promise<void>}
 */
const notifications_all_read = async () => {
  await BookingNotificationsService.all_read({ ...(await clinicIdParams()) });

  // 通知一覧再描画
  await loadBookingNotifications();
  // ヘッダーバッジ再描画
  window.dispatchEvent(new Event("REFRESH_NOTIFY_COUNT"));
};

/** コンポーネントのマウント前に通知リストを読み込む */
onBeforeMount(() => loadList());

return (_ctx: any,_cache: any) => {
  const _component_Button = _resolveComponent("Button")!
  const _component_ProgressSpinner = _resolveComponent("ProgressSpinner")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(Paginator, {
            id: "pg-top",
            ref_key: "pgTop",
            ref: pgTop,
            modelValue: first.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((first).value = $event)),
            onPaging: paging,
            totalRecords: totalCount.value,
            rows: rows.value,
            paginatorId: "pg-top"
          }, null, 8, ["modelValue", "totalRecords", "rows"]),
          _createVNode(_component_Button, {
            class: "create-btn",
            label: "予約履歴を既読にする",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (notifications_all_read()))
          })
        ]),
        _createElementVNode("div", _hoisted_6, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columns.value, (column, idx) => {
            return (_openBlock(), _createElementBlock("div", {
              class: _normalizeClass(`${column?.class || 'col'}`),
              style: _normalizeStyle(`${column?.style || ''}`),
              key: idx
            }, _toDisplayString(column.header), 7))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_7, [
          (bookingNotifications.value.length > 0)
            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(bookingNotifications.value, (item, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: "grid grid-nogutter grid-content",
                  key: index
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columns.value, (column, idx) => {
                    return (_openBlock(), _createElementBlock("div", {
                      class: _normalizeClass([column?.class || 'col', { 'bg-viewed': item['viewed'] }]),
                      style: _normalizeStyle(`${column?.style || ''}`),
                      key: idx
                    }, [
                      _createElementVNode("div", {
                        class: "column-content flex align-content-center flex-wrap h-full",
                        onClick: ($event: any) => (bookingDetail(item))
                      }, [
                        (column.field === 'booking_info')
                          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(item[column.field], (info, index) => {
                              return (_openBlock(), _createBlock(_unref(Tag), {
                                key: index,
                                class: "mr-2 booking-detail mt-2 booking-notifications-tag",
                                value: "Contrast"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(info), 1)
                                ]),
                                _: 2
                              }, 1024))
                            }), 128))
                          : (column.field === 'title' && item['owner'] == 'customer')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createElementVNode("span", {
                                  class: _normalizeClass(["inline-block customer-status border-round-xl text-center mr-1 px-2", item['customer_status']])
                                }, _toDisplayString(item["customer_status_name"]), 3),
                                _createElementVNode("span", _hoisted_9, _toDisplayString(item[column.field]), 1)
                              ], 64))
                            : (column.field === 'action_type_name')
                              ? (_openBlock(), _createElementBlock("span", {
                                  key: 2,
                                  class: _normalizeClass(["booking-detail border-none", bookingNormalBackgroundColor()[item['action_type']]])
                                }, _toDisplayString(item[column.field]), 3))
                              : (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(item[column.field]), 1))
                      ], 8, _hoisted_8)
                    ], 6))
                  }), 128))
                ]))
              }), 128))
            : (loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  _createElementVNode("div", _hoisted_12, [
                    _createVNode(_component_ProgressSpinner, {
                      class: "loading-icon",
                      strokeWidth: "3",
                      animationDuration: ".5s"
                    })
                  ])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_13, "表示できる履歴はありません。"))
        ]),
        _createVNode(Paginator, {
          id: "pg-bot",
          ref_key: "pgBot",
          ref: pgBot,
          modelValue: first.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((first).value = $event)),
          onPaging: paging,
          totalRecords: totalCount.value,
          rows: rows.value,
          class: "mt-2",
          paginatorId: "pg-bot"
        }, null, 8, ["modelValue", "totalRecords", "rows"])
      ])
    ])
  ]))
}
}

})